import axios from "axios";
import { atom, useRecoilValue, useSetRecoilState } from "recoil";

import { geckoUrl } from "../utils/constants";
import { networkState } from "./networkState";
import { ChainInfo, contractInfo, PoolInfo, TokenInfo } from "../types/configTypes";

import mainnetPoolsList from "../config/mainnet/pools_info.json";
import mainnetChainInfo from "../config/mainnet/chain_info.json";
import mainnetAssetsList from "../config/mainnet/assets_info.json";
import mainnetIbcChainList from "../config/mainnet/ibc_chain_info.json";
import mainnetOtherContracts from "../config/mainnet/other_contracts.json";

import testnetPoolsList from "../config/testnet/pools_info.json";
import testnetChainInfo from "../config/testnet/chain_info.json";
import testnetAssetsList from "../config/testnet/assets_info.json";
import testnetIbcChainList from "../config/testnet/ibc_chain_info.json";
import testnetOtherContracts from "../config/testnet/other_contracts.json";

export const useFetchPrices = () => {
  const setPriceState = useSetRecoilState(priceState);
  const {network} = useRecoilValue(networkState);

  const assetsList = (network === "mainnet")? mainnetAssetsList: testnetAssetsList;

  return async () => {
    const geckoIdList = assetsList.assets.map((asset: any) => {
      return asset.coingecko_id;
    });
    const priceResponse = await axios.get(geckoUrl, {
      params: { ids: [...Array.from(new Set(geckoIdList))].join(), vs_currencies: `usd` },
    });

    setPriceState({
      priceMap: priceResponse.data,
    });
  };
};

const readConfig = () => {
  // use it when we have generator for
  // const network = (localStorage.getItem("networkState")!) === null ? "testnet" : (localStorage.getItem("networkState")!);

  // for testnet only
  const network:string = "testnet";

  const poolsList = (network === "mainnet")? mainnetPoolsList: testnetPoolsList;
  const chainInfo = (network === "mainnet")? mainnetChainInfo: testnetChainInfo;
  const assetsList = (network === "mainnet")? mainnetAssetsList: testnetAssetsList;
  const ibcChainList = (network === "mainnet")? mainnetIbcChainList: testnetIbcChainList;
  const otherContracts = (network === "mainnet")? mainnetOtherContracts: testnetOtherContracts;
  
  let poolsMap: Record<string, PoolInfo> = {};
  poolsList.pairs.forEach((pair) => {
    poolsMap[pair.pair_name] = pair;
  });

  let assetsMap: Record<string, TokenInfo> = {};
  assetsList.assets.forEach((asset: any) => {
    assetsMap[asset.display_denom] = asset;
  });

  let ibcChainInfoMap: Record<string, ChainInfo> = {};
  ibcChainList.chains.forEach((chain) => {
    ibcChainInfoMap[chain.stakeCurrency.coinDenom] = chain;
  });

  let otherContractsMap: Record<string, contractInfo> = {};
  Object.entries(otherContracts).forEach(([contractName, value]) => {
    otherContractsMap[contractName] = value;
  });

  return {
    disclaimerSeen: false,
    tourSeen: false,
    poolsMap: poolsMap,
    ibcChainInfo: ibcChainInfoMap,
    assetsMap: assetsMap,
    chainInfo: chainInfo,
    otherContractsMap: otherContractsMap,
    userMap: undefined,
  };
};

export const configState = atom<{
  disclaimerSeen: boolean;
  tourSeen: boolean;
  poolsMap: Record<string, PoolInfo>;
  ibcChainInfo: Record<string, ChainInfo>;
  assetsMap: Record<string, TokenInfo>;
  chainInfo: ChainInfo;
  otherContractsMap: Record<string, contractInfo>;
  userMap: any;
}>({
  key: "configState",
  default: readConfig(),
  dangerouslyAllowMutability: true,
});

export const priceState = atom<{
  priceMap: any;
}>({
  key: "priceState",
  default: {
    priceMap: {},
  },
  dangerouslyAllowMutability: true,
});
